import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import { ColumnTypeEnum, OperatorEnum } from '@y42/json-schema';
import { SelectOption } from '@y42/util/select-option';
import { combineLatest, debounceTime, filter, map, Observable, startWith } from 'rxjs';
import { OperatorForm } from '../../../operation-input.model';
import { operationInputDefaultOperatorOptions } from '../../operation-input-operator-form-control/operation-input-default-operator-options';

@UntilDestroy()
@Component({
  selector: 'y42-operation-input-select-form-group[columnType][form][index][predefinedValues]',
  templateUrl: './operation-input-select-form-group.component.html',
  styleUrls: ['../../operation-input-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationInputSelectFormGroupComponent {
  @Input() form!: FormGroup<OperatorForm>;
  @Input() index!: number;

  @Input()
  columnType!: ColumnTypeEnum;

  @Input()
  @InputObservable()
  predefinedValues: string[] | null = null;
  private readonly predefinedValues$!: Observable<string[] | null>;

  @Input()
  @InputObservable()
  operatorOptions?: Array<SelectOption<OperatorEnum>>;
  operatorOptions$!: Observable<Array<SelectOption<OperatorEnum>> | undefined>;

  @Output()
  loadAutoProposal: EventEmitter<void> = new EventEmitter();

  readonly OperatorEnum = OperatorEnum;
  readonly trackByValue = trackByValue;

  private readonly defaultOperatorOptions: Array<SelectOption<OperatorEnum>> = operationInputDefaultOperatorOptions;

  readonly searchFormControl = new FormControl('', { nonNullable: true });

  readonly predefinedValuesWithHidden$ = combineLatest([
    this.predefinedValues$,
    this.searchFormControl.valueChanges.pipe(startWith(''), debounceTime(150)),
  ]).pipe(
    filter(([predefinedValues]) => !!predefinedValues?.length),
    map(([predefinedValues, search]) => predefinedValues!.filter((value) => this.filterSearchKey(value, search))),
    startWith([]),
  );

  readonly trackByValueProp = trackByValue;

  readonly state = connectState(this, {
    operatorOptions: this.operatorOptions$.pipe(map((options) => options ?? this.defaultOperatorOptions)),
  });

  private filterSearchKey(value: unknown, searchValue: string) {
    if (!searchValue) {
      return true;
    }

    return `${value}`.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
  }
}
